import React, { useEffect, useState } from 'react'
import {
  Typography,
  Box,
  Tabs,
  Tab,
  Paper,
  Button,
  Switch,
  TextField,
  FormControlLabel
} from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus, faCircle } from '@fortawesome/free-solid-svg-icons'
import PropTypes from 'prop-types'
import { useSmsAutomationReducer } from '../../hooks/useSmsAutomationReducer'
import { useSmsAutomationAPI } from '../../hooks/useSmsAutomationAPI'
import { useToast } from '../../hooks'
import { ConfirmDialog, LoadingSpinner, TabPanel, ToastNotification } from '../../components/atoms'
import SmsAutomationCard from '../../components/smsAutomations/SmsAutomationCard'
import SmsAutomationDialog from '../../components/smsAutomations/SmsAutomationDialog'
import SmsTestDialog from '../../components/smsAutomations/SmsTestDialog'
import SmsHistoryTab from '../../components/smsAutomations/SmsHistoryTab'
import CampaignsDialog from '../../components/notifications/CampaignsDialog'
import styles from './SettingsSmsAutomations.module.scss'

const SettingsSmsAutomations = ({ userID }) => {
  const [state, actions] = useSmsAutomationReducer()
  const api = useSmsAutomationAPI(userID, actions)
  const { toast, showToast, closeToast } = useToast()
  const [historyAutomationId, setHistoryAutomationId] = useState(null)
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null })

  useEffect(() => {
    api.fetchSmsAutomations()
    api.fetchDispositions()
    api.fetchSmsSettings()
    api.fetchAllCampaigns()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (state.error) {
      showToast(state.error, 'error')
      actions.setError(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.error])

  useEffect(() => {
    if (state.tabValue === 1 && state.automations.length > 0) {
      if (!historyAutomationId) {
        setHistoryAutomationId(state.automations[0].id)
        return
      }
      api.fetchSmsHistory(historyAutomationId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.tabValue, state.automations, historyAutomationId])

  const handleCreateAutomation = () => {
    actions.resetNewAutomation()
    actions.toggleDialog('createDialogOpen', true)
  }

  const handleEditAutomation = (automation) => {
    actions.setSelectedAutomation(automation)
    actions.toggleDialog('editDialogOpen', true)
  }

  const handleSaveAutomation = async (automation) => {
    const success = automation.id
      ? await api.updateAutomation(automation)
      : await api.createAutomation(automation)

    if (success) {
      actions.toggleDialog('createDialogOpen', false)
      actions.toggleDialog('editDialogOpen', false)
      actions.setSelectedAutomation(null)
      actions.resetNewAutomation()
    }

    return success === false ? false : undefined
  }

  const handleDeleteAutomation = (automationId) => {
    setConfirmDialog({
      open: true,
      title: 'Delete SMS Automation',
      message: 'Are you sure you want to delete this SMS automation? This will also remove all associated send history. This action cannot be undone.',
      confirmColor: 'error',
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, open: false }))
        await api.deleteAutomation(automationId)
      }
    })
  }

  const handleToggleAutomation = async (automationId, enabled) => {
    await api.toggleAutomation(automationId, enabled)
  }

  const handleTestAutomation = (automation) => {
    actions.setSelectedAutomation(automation)
    actions.toggleDialog('testDialogOpen', true)
  }

  const handleSendTest = async (phoneNumber) => {
    if (!state.selectedAutomation) return { success: false, error: 'No automation selected' }
    return await api.testSms(state.selectedAutomation.id, phoneNumber)
  }

  const handleManageCampaigns = async (automation) => {
    actions.setSelectedAutomation(automation)
    await api.fetchCampaignsForAutomation(automation.id)
    actions.toggleDialog('campaignsDialogOpen', true)
  }

  const handleToggleCampaign = async (campaignName, enabled) => {
    if (state.selectedAutomation) {
      await api.toggleCampaign(state.selectedAutomation.id, campaignName, enabled)
    }
  }

  const handleToggleAllCampaigns = async (enabled) => {
    if (state.selectedAutomation) {
      await api.toggleAllCampaigns(state.selectedAutomation.id, enabled)
    }
  }

  const handleOverrideToggle = async (enabled) => {
    const settings = {
      override_enabled: enabled,
      override_to_number: state.smsSettings.override_to_number || '',
      updated_by: userID
    }
    if (!enabled) {
      await api.updateSmsSettings(settings)
    }
  }

  const handleOverrideSave = async () => {
    await api.updateSmsSettings({
      override_enabled: state.smsSettings.override_enabled,
      override_to_number: state.smsSettings.override_to_number,
      max_batch_size: state.smsSettings.max_batch_size,
      max_call_age_hours: state.smsSettings.max_call_age_hours,
      updated_by: userID
    })
  }

  const handleHistoryPageChange = (page) => {
    if (historyAutomationId) {
      api.fetchSmsHistory(historyAutomationId, page)
    }
  }

  const handleHistoryAutomationChange = (automationId) => {
    setHistoryAutomationId(automationId)
  }

  const closeCreateDialog = () => {
    actions.toggleDialog('createDialogOpen', false)
    actions.resetNewAutomation()
  }

  const closeEditDialog = () => {
    actions.toggleDialog('editDialogOpen', false)
    actions.setSelectedAutomation(null)
  }

  const closeTestDialog = () => {
    actions.toggleDialog('testDialogOpen', false)
    actions.setSelectedAutomation(null)
  }

  const closeCampaignsDialog = () => {
    actions.toggleDialog('campaignsDialogOpen', false)
    actions.setSelectedAutomation(null)
  }

  return (
    <div className={styles.root}>
      <Box className={styles.pageHeader}>
        <Box display='flex' alignItems='center' style={{ gap: 12 }}>
          <Typography variant='h4' component='h1' className={styles.pageTitle}>
            SMS Automations
          </Typography>
          <span className={state.smsSettings.sms_enabled ? styles.statusBadgeEnabled : styles.statusBadgeDisabled}>
            <FontAwesomeIcon icon={faCircle} style={{ fontSize: 8, marginRight: 6 }} />
            {state.smsSettings.sms_enabled ? 'SMS Enabled' : 'SMS Disabled'}
          </span>
        </Box>
        <Typography className={styles.pageSubtitle}>
          Manage automated SMS notifications triggered by call dispositions
        </Typography>
      </Box>

      <Paper className={styles.tabsPaper}>
        <Tabs
          value={state.tabValue}
          onChange={(e, newValue) => actions.setTabValue(newValue)}
          indicatorColor='primary'
          textColor='primary'
        >
          <Tab label='Automations' id='sms-tab-0' aria-controls='sms-tabpanel-0' />
          <Tab label='Send History' id='sms-tab-1' aria-controls='sms-tabpanel-1' />
        </Tabs>

        <TabPanel value={state.tabValue} index={0} idPrefix='sms'>
          <Paper variant='outlined' className={styles.overrideSection}>
            <Box display='flex' alignItems='center' justifyContent='space-between' flexWrap='wrap' style={{ gap: 16 }}>
              <Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={state.smsSettings.override_enabled || false}
                      onChange={(e) => {
                        actions.setSmsSettings({
                          ...state.smsSettings,
                          override_enabled: e.target.checked
                        })
                        if (!e.target.checked) handleOverrideToggle(false)
                      }}
                      color='secondary'
                    />
                  }
                  label={
                    <Typography variant='subtitle2'>
                      Phone Number Override (Testing)
                    </Typography>
                  }
                />
                <Typography variant='caption' color='textSecondary' style={{ display: 'block', marginLeft: 48 }}>
                  When enabled, all automated SMS messages are redirected to this number instead of the caller
                </Typography>
              </Box>
              {state.smsSettings.override_enabled && (
                <Box display='flex' alignItems='center' style={{ gap: 8 }}>
                  <TextField
                    size='small'
                    variant='outlined'
                    label='Override Number'
                    placeholder='+1234567890'
                    value={state.smsSettings.override_to_number || ''}
                    onChange={(e) => actions.setSmsSettings({
                      ...state.smsSettings,
                      override_to_number: e.target.value
                    })}
                    style={{ width: 200 }}
                  />
                  <Button
                    variant='contained'
                    color='secondary'
                    size='small'
                    onClick={handleOverrideSave}
                    disabled={!state.smsSettings.override_to_number}
                  >
                    Save
                  </Button>
                </Box>
              )}
            </Box>
          </Paper>

          <Paper variant='outlined' className={styles.overrideSection}>
            <Typography variant='subtitle2' style={{ marginBottom: 8 }}>
              Safety Limits
            </Typography>
            <Typography variant='caption' color='textSecondary' style={{ display: 'block', marginBottom: 12 }}>
              Controls to prevent mass SMS sends during data syncs
            </Typography>
            <Box display='flex' alignItems='center' flexWrap='wrap' style={{ gap: 16 }}>
              <TextField
                size='small'
                variant='outlined'
                label='Max SMS per sync batch'
                type='number'
                inputProps={{ min: 1, max: 1000 }}
                value={state.smsSettings.max_batch_size ?? 50}
                onChange={(e) => actions.setSmsSettings({
                  ...state.smsSettings,
                  max_batch_size: parseInt(e.target.value) || 50
                })}
                style={{ width: 200 }}
                helperText='1 - 1000'
              />
              <TextField
                size='small'
                variant='outlined'
                label='Max call age (hours)'
                type='number'
                inputProps={{ min: 1, max: 72 }}
                value={state.smsSettings.max_call_age_hours ?? 4}
                onChange={(e) => actions.setSmsSettings({
                  ...state.smsSettings,
                  max_call_age_hours: parseInt(e.target.value) || 4
                })}
                style={{ width: 200 }}
                helperText='1 - 72 hours'
              />
              <Button
                variant='contained'
                color='primary'
                size='small'
                onClick={handleOverrideSave}
              >
                Save Limits
              </Button>
            </Box>
          </Paper>

          <Box display='flex' justifyContent='space-between' alignItems='center' flexWrap='wrap' style={{ gap: 12 }} mb={3}>
            <Typography variant='h6'>SMS Automations</Typography>
            {state.automations.length > 0 && (
              <Button
                variant='contained'
                color='primary'
                startIcon={<FontAwesomeIcon icon={faPlus} />}
                onClick={handleCreateAutomation}
              >
                New SMS Automation
              </Button>
            )}
          </Box>

          {state.loading && state.automations.length === 0 ? (
            <LoadingSpinner />
          ) : state.automations.length === 0 ? (
            <Box textAlign='center' p={4}>
              <Typography variant='h6' color='textSecondary' gutterBottom>
                No SMS automations configured
              </Typography>
              <Typography variant='body2' color='textSecondary' paragraph>
                Create your first SMS automation to send notifications when specific dispositions are triggered.
              </Typography>
              <Button
                variant='contained'
                color='primary'
                startIcon={<FontAwesomeIcon icon={faPlus} />}
                onClick={handleCreateAutomation}
              >
                Create First Automation
              </Button>
            </Box>
          ) : (
            <div className={styles.automationsGrid}>
              {state.automations.map((automation) => (
                <SmsAutomationCard
                  key={automation.id}
                  automation={automation}
                  onEdit={handleEditAutomation}
                  onDelete={handleDeleteAutomation}
                  onToggle={handleToggleAutomation}
                  onTest={handleTestAutomation}
                  onManageCampaigns={handleManageCampaigns}
                  className={styles.automationCard}
                />
              ))}
            </div>
          )}
        </TabPanel>

        <TabPanel value={state.tabValue} index={1} idPrefix='sms'>
          <SmsHistoryTab
            history={state.smsHistory}
            meta={state.smsHistoryMeta}
            onPageChange={handleHistoryPageChange}
            loading={state.loading}
            automations={state.automations}
            selectedAutomationId={historyAutomationId}
            onAutomationChange={handleHistoryAutomationChange}
          />
        </TabPanel>
      </Paper>

      <SmsAutomationDialog
        open={state.createDialogOpen}
        onClose={closeCreateDialog}
        automation={state.newAutomation}
        dispositions={state.dispositions}
        campaigns={state.campaigns}
        onSave={handleSaveAutomation}
        isEditing={false}
      />

      <SmsAutomationDialog
        open={state.editDialogOpen}
        onClose={closeEditDialog}
        automation={state.selectedAutomation}
        dispositions={state.dispositions}
        campaigns={state.campaigns}
        onSave={handleSaveAutomation}
        isEditing={true}
      />

      <SmsTestDialog
        open={state.testDialogOpen}
        onClose={closeTestDialog}
        automationName={state.selectedAutomation?.name}
        onSendTest={handleSendTest}
      />

      <CampaignsDialog
        open={state.campaignsDialogOpen}
        onClose={closeCampaignsDialog}
        automation={state.selectedAutomation}
        campaigns={state.campaigns}
        searchTerm={state.campaignSearchTerm}
        onSearchChange={actions.setCampaignSearchTerm}
        onToggleCampaign={handleToggleCampaign}
        onToggleAllCampaigns={handleToggleAllCampaigns}
      />

      <ConfirmDialog
        open={confirmDialog.open}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog(prev => ({ ...prev, open: false }))}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmLabel='Delete'
        confirmColor={confirmDialog.confirmColor || 'error'}
      />

      <ToastNotification
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={closeToast}
      />
    </div>
  )
}

SettingsSmsAutomations.propTypes = {
  userID: PropTypes.string
}

export default SettingsSmsAutomations
